// [PHASE B / #006 — B3-4]: Roll-table entry validation helpers extracted
//   from CustomRollTableDialog so the test exercises production code
//   (Rule 22). Pre-fix, the dialog's handleSave did the validation
//   inline and surfaced a generic
//     "Please fill in at least one table entry with min, max, and
//      item values"
//   alert when ALL entries were rejected — leaving the user with no
//   way to diagnose which row was incomplete or which field was the
//   problem. The May 5 smoke-test report documented the alert firing
//   "despite all entries having values" — almost always one of:
//     - The user typed in min/max but left the <select> on the empty
//       "Select Item..." placeholder (item value `''` is falsy).
//     - The user typed then deleted a numeric field (parseInt('') is NaN).
//     - The user filled some rows and left others blank (silently
//       skipped under the prior contract; if every row was bad the
//       generic alert fired).
//   The new diagnostic enumerates every row with at least one missing
//   field so the user can act on the alert directly.

/**
 * Validate one row of a custom roll table.
 *
 * @param {{min, max, item, rarity}} entry
 * @returns {{
 *   idx: number,
 *   min: number,
 *   max: number,
 *   item: string,
 *   rarity: string,
 *   missing: string[],
 *   isValid: boolean,
 * }}
 */
export function validateRollTableEntry(entry, idx) {
  const min = parseInt(entry?.min, 10);
  const max = parseInt(entry?.max, 10);
  const missing = [];
  if (!Number.isFinite(min)) missing.push('min');
  if (!Number.isFinite(max)) missing.push('max');
  if (!entry?.item) missing.push('item');
  return {
    idx,
    min,
    max,
    item: entry?.item,
    rarity: entry?.rarity,
    missing,
    isValid: missing.length === 0,
  };
}

/**
 * Validate every row in a roll-table draft. Returns the per-row
 * validation array AND the array of valid rolls ready for persistence.
 *
 * @param {Array} entries
 * @returns {{ validations: Array, validRolls: Array }}
 */
export function validateRollTableEntries(entries) {
  const validations = (entries || []).map((e, idx) => validateRollTableEntry(e, idx));
  const validRolls = validations
    .filter((v) => v.isValid)
    .map((v) => ({
      min: v.min,
      max: v.max,
      item: v.item,
      rarity: v.rarity,
    }));
  return { validations, validRolls };
}

/**
 * Build the per-row diagnostic line list for the validation alert.
 * Only invalid rows are listed so a passing-but-mixed table still
 * highlights only the rows the user must address.
 *
 * @param {Array} validations - output from validateRollTableEntries
 * @returns {string}
 */
export function buildEntryDiagnostic(validations) {
  return (validations || [])
    .filter((v) => !v.isValid)
    .map((v) => `  Row ${v.idx + 1}: missing ${v.missing.join(', ')}`)
    .join('\n');
}
